import cn from "@/v2-ui/utils/utils.cn"
import Badge, {
  type BadgeVariant,
} from "@/v2-backoffice-client/components/badge"
import {
  classifyLatency,
  formatLatency,
  type LatencyClass,
} from "@/v2-backoffice-client/uptime/uptime.utils"

export type UptimeLatencyBadgeProps = {
  // Current latency observation (e.g. 24h avg or single probe).
  currentMs?: number | null
  // Per-target baseline to colour against (e.g. 90d p95). Optional;
  // component falls back to absolute bands when missing.
  baselineMs?: number | null
  // Explicit class override for callers that already classified the
  // latency themselves (e.g. the overview card needs "unknown" styling
  // even with a concrete value).
  variant?: LatencyClass
  // Tooltip label override. Defaults to a concise baseline summary.
  tooltip?: string
  className?: string
}

const VARIANT_LABEL: Record<LatencyClass, string> = {
  unknown: "No recent data",
  fast: "Fast",
  normal: "Normal",
  slow: "Slow",
  critical: "Much slower than baseline",
}

const LATENCY_VARIANTS: Record<LatencyClass, BadgeVariant> = {
  unknown: "neutral",
  fast: "positive",
  normal: "neutral",
  slow: "warning",
  critical: "critical",
}

function UptimeLatencyBadge(props: UptimeLatencyBadgeProps) {
  const { currentMs, baselineMs, variant, tooltip, className } = props

  const klass: LatencyClass = variant ?? classifyLatency(currentMs, baselineMs)
  const label = formatLatency(currentMs)

  const defaultTooltip = (() => {
    if (klass === "unknown") return VARIANT_LABEL.unknown
    if (baselineMs == null) return `${VARIANT_LABEL[klass]} • no baseline`
    const ratio = currentMs && baselineMs ? currentMs / baselineMs : 0
    return `${VARIANT_LABEL[klass]} • baseline ${formatLatency(baselineMs)} (${ratio.toFixed(1)}x)`
  })()

  return (
    <Badge
      variant={LATENCY_VARIANTS[klass]}
      // "Normal" reads as a real observation, not a missing one — keep
      // its text at full strength to stay distinct from "unknown".
      className={cn(klass === "normal" && "text-foreground", className)}
      tooltip={tooltip ?? defaultTooltip}
    >
      {label}
    </Badge>
  )
}

export default UptimeLatencyBadge
